import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { BareImage, Image, ImageVariant } from '@go-shared/types/image';
import { BubbleTypeaheadValue } from '../BubbleUI/BubbleTypeahead';
import { BrandImageType } from '@go-shared/types/brand';
import uuid from 'uuid';
import { mapBy, MappedItems, notUndefined } from '@go-shared/lib/functional';
import { Form, FormikErrors, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import * as Yup from 'yup';
import { ObjectID } from 'bson';
import { createImage, updateImage } from '@go-client/lib/api/image';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleImageThumbnail,
  BubbleInput,
  BubbleTextArea,
  BubbleTypeahead,
} from '../BubbleUI';
import { createPortal } from 'react-dom';
import { uploadImage, uploadImageVariant } from '@go-client/lib/api/brand';
import { CraftArtifact, NO_IMAGE_KEY } from '@go-client/context/ImageCropperContext';
import { getImageRequirementOptions, getMissingImageRequirements } from '@go-shared/lib/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canDeleteImages, canManageLibraries } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { fme } from '@go-shared/lib/utils';
import { useBubbleDrawerTools } from '../BubbleUI/BubbleDrawer';

export interface ImageFormikMode {
  type: 'create' | 'edit' | 'view';
  page: 'edit' | 'insights';
  edit?: Image;
}

interface ImageFormikForm {
  key: string;
  name: string;
  description: string;
  extension: string;
  mimeType: string;
  size: number;
  imageTypes: Array<BubbleTypeaheadValue<BrandImageType>>;
  variants: Array<
    Omit<ImageVariant, 'imageType'> & {
      imageTypes: Array<BubbleTypeaheadValue<BrandImageType>>;
    }
  >;
}

export interface ImgFormikExtState {
  values: ImageFormikForm;
  disabled: boolean;
}

interface ImageFormikPropsBase {
  mode: ImageFormikMode;
  actionsElem?: HTMLDivElement;
  actionsClassName?: string;
  actionsAppearance?: 'drawer-float';
  subnavClassName?: string;
  skipBasic?: boolean;
  onSubmit?: (template: BareImage) => void;
  onStateUpdate?: (state: ImgFormikExtState) => void;
  onDeleteImageClick?: () => void;
}

export interface ImageFormikRef {
  reset: () => void;
  submit: () => void;
  dirty: () => boolean;
}

type ImageFormikProps = ImageFormikPropsBase;

function GENERATE_INITIAL_VALUES(
  imageTypeMap: MappedItems<{
    key: string;
    item: BrandImageType;
  }>,
  edit?: Image
): ImageFormikForm {
  const values: ImageFormikForm = {
    key: edit?.key ?? NO_IMAGE_KEY,
    name: edit?.name ?? '',
    description: edit?.description ?? '',
    extension: edit?.extension ?? '',
    mimeType: edit?.mimeType ?? '',
    size: edit?.size ?? 0,
    imageTypes: (edit?.imageType ?? [])
      .map(k => {
        const match = imageTypeMap[k._id];

        if (!match) return undefined;

        return {
          key: uuid(),
          refKey: match.key,
          item: match.item,
        };
      })
      .filter(notUndefined),
    variants: (edit?.variants ?? []).map(variant => ({
      ...variant,
      imageTypes: variant.imageType
        .map(k => {
          const match = imageTypeMap[k];

          if (!match) return undefined;

          return {
            key: uuid(),
            refKey: match.key,
            item: match.item,
          };
        })
        .filter(notUndefined),
    })),
  };

  return values;
}

function ImageFormik(props: ImageFormikProps, ref: React.ForwardedRef<ImageFormikRef>) {
  const { brand, plugins } = useServerInfo();
  const { user } = useUserInfo();
  const { scrollToBottom } = useBubbleDrawerTools();

  const imageTypeOpts = brand.imageTypes.map(t => ({
    key: t._id,
    item: t,
  }));
  const imageTypeMap = mapBy(imageTypeOpts, 'key');

  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(imageTypeMap, props.mode.edit),
    validate: async values => {
      let errors: FormikErrors<ImageFormikForm> = {};

      const basicSchema = Yup.object({
        name: Yup.string()
          .required('Required')
          .test(
            'not-no-image-key',
            'Invalid image key',
            value => !!value && !value.startsWith(NO_IMAGE_KEY)
          ),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<ImageFormikForm>(err);
        errors = { ...errors, ...t };
      }

      return errors;
    },
    onSubmit: onFormSubmit,
  });

  useEffect(() => {
    formik.resetForm({
      values: GENERATE_INITIAL_VALUES(imageTypeMap, props.mode.edit),
    });
  }, [props.mode.edit]);

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange, handleBlur, dirty } =
    formik;

  const actionClasses = [];
  if (props.actionsAppearance === 'drawer-float')
    actionClasses.push('sticky mt-3 bottom-0 z-20 -mx-8 p-8 pt-4 form-blur');
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  const subnavClasses = ['flex items-center mb-6'];
  if (props.subnavClassName) subnavClasses.push(props.subnavClassName);

  async function onFormSubmit(values: ImageFormikForm) {
    let newImage: BareImage = {
      ...(props.mode.edit || {
        _id: new ObjectID().toHexString(),
      }),
      key: values.key,
      name: values.name,
      description: values.description,
      extension: values.extension,
      mimeType: values.mimeType,
      size: values.size,
      imageType: values.imageTypes.map(t => t.item._id),
      variants: values.variants.map(v => ({
        ...v,
        imageType: v.imageTypes.map(t => t.item._id),
      })),
    };

    switch (props.mode.type) {
      case 'create':
        const { _id, ...rest } = newImage;
        newImage = await createImage(rest);
        break;
      case 'edit':
        newImage = await updateImage(newImage);
        break;
    }

    if (props.onSubmit) props.onSubmit(newImage);
  }

  const canEdit = canManageLibraries(user) || canDeleteImages(user);
  const canDelete = canDeleteImages(user);
  const locked = !canEdit;
  const readOnly = props.mode.type === 'view' || locked;
  const disabled =
    readOnly ||
    locked ||
    formik.isSubmitting ||
    !formik.isValid ||
    !dirty ||
    formik.values.key.startsWith(NO_IMAGE_KEY);
  const variantDisabled =
    readOnly || locked || formik.isSubmitting || formik.values.key.startsWith(NO_IMAGE_KEY);
  const deleteDisabled = readOnly || !canDelete || formik.isSubmitting;

  const formActions = locked ? (
    <></>
  ) : (
    <div className="flex items-center">
      <BubbleButton disabled={variantDisabled} variant="slate" onClick={addVariant} icon="plus">
        Add Image Format
      </BubbleButton>
      <div className="ml-auto" />
      {props.mode.type !== 'create' && !locked && (
        <BubbleButton
          disabled={deleteDisabled}
          variant="crimson"
          inverse
          icon="trash-alt"
          onClick={props.onDeleteImageClick}
        >
          Delete
        </BubbleButton>
      )}
      <BubbleButton disabled={disabled} variant="navy" type="reset" inverse icon="rotate-left">
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        type="submit"
        disabled={disabled}
        icon={props.mode.type === 'edit' ? 'floppy-disk' : 'sparkles'}
      >
        {props.mode.type === 'edit' ? 'Save Changes' : 'Create Image'}
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem
    ? createPortal(formActions, props.actionsElem)
    : formActions;

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
    dirty: () => formik.dirty,
  }));

  useEffect(() => {
    if (props.onStateUpdate)
      props.onStateUpdate({
        values: formik.values,
        disabled,
      });
  }, [formik.values, disabled, props.mode]);

  function addVariant() {
    setFieldValue('variants', [
      ...values.variants,
      {
        _id: new ObjectID().toHexString(),
        key: `${NO_IMAGE_KEY}${uuid()}`,
        imageTypes: [],
      },
    ]);
    scrollToBottom();
  }

  async function handleOnCraftImage(artifacts: CraftArtifact[]) {
    const [mainArtifact, ...variantArtifacts] = artifacts;

    const file = new File([mainArtifact.blob], [mainArtifact.name, 'png'].join('.'), {
      type: mainArtifact.blob.type,
    });
    const response = await uploadImage(brand._id, file);

    setFieldValue('extension', 'png');
    setFieldValue('mimeType', 'image/png');
    setFieldValue('size', mainArtifact.size);
    setFieldValue('key', response.location);

    const newVariants: Array<
      Omit<ImageVariant, 'imageType'> & {
        imageTypes: BubbleTypeaheadValue<BrandImageType>[];
      }
    > = [];

    for (const variantArtifact of variantArtifacts) {
      const file = new File([variantArtifact.blob], [variantArtifact.name, 'png'].join('.'), {
        type: variantArtifact.blob.type,
      });
      const response = await uploadImageVariant(brand._id, file);
      newVariants.push({
        _id: new ObjectID().toHexString(),
        imageTypes: [],
        key: response.location,
        extension: 'png',
        size: variantArtifact.size,
        mimeType: 'image/png',
        optimization: variantArtifact.optimization,
      });
    }

    if (newVariants.length > 0) {
      setFieldValue('variants', newVariants);
    }
  }

  async function handleOnCraftVariant(artifact: CraftArtifact, variantIdx: number) {
    const file = new File([artifact.blob], [artifact.name, 'png'].join('.'), {
      type: artifact.blob.type,
    });
    const response = await uploadImageVariant(brand._id, file);

    const updatedVariants = [...values.variants];

    updatedVariants[variantIdx] = {
      ...updatedVariants[variantIdx],
      key: response.location,
      extension: 'png',
      size: artifact.size,
      mimeType: 'image/png',
      optimization: artifact.optimization,
    };

    setFieldValue('variants', updatedVariants);
  }

  const requirementOpts = mapBy(getImageRequirementOptions(plugins), 'optimization');
  const missingImageRequirements = getMissingImageRequirements(
    plugins,
    values.variants || props.mode.edit?.variants
  );

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit}>
        {locked && (
          <BubbleAlert className="mb-3" variant="ocean">
            <span className="font-semibold">This template cannot be modified.</span> It may be a
            brand level event or you lack library permissions.
          </BubbleAlert>
        )}
        {!(props.mode.edit?.key || values.key).startsWith(NO_IMAGE_KEY) &&
          missingImageRequirements.length > 0 && (
            <BubbleAlert variant="banana" className="mb-3">
              <div className="text-left">
                <FontAwesomeIcon icon="tachometer-slowest" className="mr-1" />{' '}
                <span className="font-semibold">This image may cause long loading times!</span>{' '}
                Please add image formats for the following optimizations:
                <ul className="list-disc pl-6 mt-2">
                  {missingImageRequirements.map(req => (
                    <li key={req.optimization}>
                      <span className="font-semibold">{req.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </BubbleAlert>
          )}
        <div className="grid grid-cols-1 gap-4">
          <div className="flex items-start space-x-4">
            <BubbleImageThumbnail
              className="w-56"
              readOnly={locked}
              purpose={props.mode.edit ? 'main' : 'first'}
              thumbnail={values || props.mode.edit}
              onCraft={handleOnCraftImage}
            />
            <div className="flex-grow flex flex-col space-y-4">
              <BubbleFormGroup label="Name" asterisk error={fme(errors, touched, 'name')}>
                <BubbleInput
                  block
                  type="text"
                  name="name"
                  readOnly={readOnly}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Image Types">
                <BubbleTypeahead
                  block
                  placeholder="Search..."
                  menuClassName="max-h-80 overflow-y-auto"
                  labelExtractor={opt => opt.name}
                  value={values.imageTypes}
                  options={imageTypeOpts}
                  readOnly={readOnly}
                  onAdd={t => setFieldValue('imageTypes', [...values.imageTypes, t])}
                  onRemove={t =>
                    setFieldValue(
                      'imageTypes',
                      values.imageTypes.filter(t2 => t.key !== t2.key)
                    )
                  }
                  onClearAll={() => setFieldValue('imageTypes', [])}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Description" className="col-span-2">
                <BubbleTextArea
                  block
                  name="description"
                  readOnly={readOnly}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.description}
                />
              </BubbleFormGroup>
            </div>
          </div>
          {!formik.values.key.startsWith(NO_IMAGE_KEY) && formik.values.variants.length > 0 && (
            <>
              <h2 className="text-xl text-slate mt-3">Image Formats</h2>
              {formik.values.variants.map((variant, idx) => (
                <div key={variant._id} className="flex items-start space-x-4">
                  <BubbleImageThumbnail
                    purpose="variant"
                    className="w-40"
                    thumbnail={variant}
                    readOnly={locked}
                    variants={formik.values.variants}
                    editDefaultKey={formik.values.key}
                    editDefaultOptimization={variant.optimization}
                    onCraft={([artifact]) => handleOnCraftVariant(artifact, idx)}
                    // You may want to handle image upload and update variant.image here
                  />
                  <div className="flex-grow flex flex-col space-y-4">
                    <BubbleFormGroup label="Optimization" asterisk>
                      <BubbleInput
                        block
                        type="text"
                        readOnly
                        readOnlyLabel="None (edit image to set)"
                        value={requirementOpts[variant.optimization]?.name}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Image Types">
                      <BubbleTypeahead
                        block
                        placeholder="Search..."
                        menuClassName="max-h-80 overflow-y-auto"
                        labelExtractor={opt => opt.name}
                        value={variant.imageTypes}
                        options={imageTypeOpts}
                        readOnly={readOnly}
                        onAdd={t => {
                          const updatedVariants = [...formik.values.variants];
                          updatedVariants[idx] = {
                            ...variant,
                            imageTypes: [...variant.imageTypes, t],
                          };
                          setFieldValue('variants', updatedVariants);
                        }}
                        onRemove={t => {
                          const updatedVariants = [...formik.values.variants];
                          updatedVariants[idx] = {
                            ...variant,
                            imageTypes: variant.imageTypes.filter(t2 => t.key !== t2.key),
                          };
                          setFieldValue('variants', updatedVariants);
                        }}
                        onClearAll={() => {
                          const updatedVariants = [...formik.values.variants];
                          updatedVariants[idx] = {
                            ...variant,
                            imageTypes: [],
                          };
                          setFieldValue('variants', updatedVariants);
                        }}
                      />
                    </BubbleFormGroup>
                  </div>
                  {(canDelete || props.mode.type === 'create') && (
                    <BubbleButton
                      variant="crimson"
                      inverse
                      icon="trash-alt"
                      className="mt-5"
                      onClick={() => {
                        const updatedVariants = formik.values.variants.filter((_, i) => i !== idx);
                        setFieldValue('variants', updatedVariants);
                      }}
                      disabled={readOnly}
                    />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
        {props.mode.type !== 'view' && (
          <div className={actionClasses.join(' ')}>{portalButtons}</div>
        )}
      </Form>
    </FormikProvider>
  );
}

export default forwardRef<ImageFormikRef, ImageFormikProps>(ImageFormik);
